/**
 * The floor plan editors' per-machine preferences, the same six on every
 * platform: what the Settings and View menus read and write. Adding one is a
 * field here, a default, a line in `parseFloorPlanPreferences`, and a row in
 * the shared menus (`floorPlanMenus`). Where they are kept is each
 * platform's storage (`floorPlanPreferencesStore`).
 */
import {
  DEFAULT_ROOM_LABEL_PREFERENCES,
  parseRoomNames,
  parseRoomSizes,
  type RoomLabelPreferences,
} from '../assembler';
import {
  ALL_PLAN_LAYERS,
  DEFAULT_MEASUREMENT_UNITS,
  isMeasurementUnits,
  type MeasurementUnits,
  parsePlanLayers,
  type PlanLayers,
} from '../editor-model';
import { DEFAULT_VIEW3D_QUALITY, parseView3DQuality, type View3DQuality } from '../view3d';

/** The editors' preferences: these, and what the assembler's room labels say (`RoomLabelPreferences`). */
export interface FloorPlanPreferences extends RoomLabelPreferences {
  /** How lengths display and are typed, everywhere in both editors. */
  units: MeasurementUnits;
  /** Whether moved things snap: rooms to neighboring walls and openings, elements to nearby edges. */
  snapping: boolean;
  /** What shows inside the walls, in both views of both editors and in their exports. */
  layers: PlanLayers;
  /** How the 3D view draws in both editors: Low or High. */
  view3dQuality: View3DQuality;
}

/** What a plan shows: the view menu's choices and the units its sizes read in. */
export type PlanViewPreferences = Pick<FloorPlanPreferences, 'layers' | 'roomSizes' | 'roomNames' | 'units'>;

export const DEFAULT_FLOOR_PLAN_PREFERENCES: FloorPlanPreferences = {
  units: DEFAULT_MEASUREMENT_UNITS,
  snapping: true,
  layers: ALL_PLAN_LAYERS,
  view3dQuality: DEFAULT_VIEW3D_QUALITY,
  ...DEFAULT_ROOM_LABEL_PREFERENCES,
};

const flag = (value: unknown, fallback: boolean): boolean => (typeof value === 'boolean' ? value : fallback);

/** The stored object with every field validated: a missing or unknown value takes its default. */
export function parseFloorPlanPreferences(raw: string | null | undefined): FloorPlanPreferences {
  let stored: unknown = null;
  try {
    stored = raw ? JSON.parse(raw) : null;
  } catch {
    stored = null;
  }
  const record = stored && typeof stored === 'object' ? stored as Record<string, unknown> : {};
  return {
    units: isMeasurementUnits(record.units) ? record.units : DEFAULT_FLOOR_PLAN_PREFERENCES.units,
    snapping: flag(record.snapping, DEFAULT_FLOOR_PLAN_PREFERENCES.snapping),
    layers: parsePlanLayers(record.layers),
    roomSizes: parseRoomSizes(record.roomSizes),
    roomNames: parseRoomNames(record.roomNames),
    view3dQuality: parseView3DQuality(record.view3dQuality),
  };
}
